import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { writeBatch, doc } from 'firebase/firestore'; // Removed setDoc and collection as batch is used
import { ensureCoordinatorDrafts } from '../../../services/agentService'; // Adjusted path
import { auth, db, storage } from '../../../firebase/firebase'; // Adjusted path
import { Container, Box, Stepper, Step, StepLabel, Typography, CircularProgress } from '@mui/material'; // Added CircularProgress
import cityTimezones from 'city-timezones';
import Step1Account from './Step1Account';
import Step2Profile from './Step2Profile';
import Step3Goals from './Step3Goals';
import Step4Consents from './Step4Consents';

const steps = ['Account', 'Profile & Lifestyle', 'Location & Goals', 'Consents'];

const MultiStepRegister = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstname: '', // Changed from firstName
    lastname: '',  // Changed from lastName
    gender: '',
    birthDate: null,
    profilePicture: null,
    height: '',
    weight: '',
    educationLevel: '',
    academicMajor: '',
    socioeconomicStatus: '',
    maritalStatus: '',
    career: '',
    householdSize: '',
    gymAccess: '',
    dietaryRestrictions: '',
    disabilities: '',
    injuries: '',
    currentHealthIssues: '',
    medications: '',
    allergies: '',
    currentCountry: '',
    currentState: '',
    currentCity: '',
    homeCountry: '',
    homeState: '',
    homeCity: '',
    goals: ['Exercise', 'Nutrition'],
    tosConsent: false, // Changed from termsOfService
    researchConsent: false, // Changed from researchUseConsent
    unitPreference: 'imperial',
  });
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      profilePicture: e.target.files[0],
    }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.tosConsent || !formData.researchConsent) {
      setError('You must accept Terms of Service and Research Use consent.');
      // Optional: Navigate back to consents step or show error more prominently
      // setCurrentStep(4); 
      return;
    }
    try {
      setUploading(true);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      let photoURL = '';
      if (formData.profilePicture) {
        const storageRef = ref(storage, `profilePictures/${user.uid}`);
        const snapshot = await uploadBytes(storageRef, formData.profilePicture);
        photoURL = await getDownloadURL(snapshot.ref);
      }

      await updateProfile(user, {
        displayName: `${formData.firstname} ${formData.lastname}`,
        photoURL,
      });

      const batch = writeBatch(db);
      const userDocRef = doc(db, 'users', user.uid); // Define userDocRef

      // Infer timezone
      let timezone = '';
      const locationString = [formData.currentCity, formData.currentState, formData.currentCountry]
        .filter(Boolean) // Remove empty parts
        .join(', ');

      if (locationString) {
        const possibleLocations = cityTimezones.findFromCityStateProvince(locationString);
        if (possibleLocations && possibleLocations.length > 0) {
          timezone = possibleLocations[0].timezone;
        }
      }

      batch.set(userDocRef, { // Use userDocRef
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        gender: formData.gender,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString().split('T')[0] : null, // Format date
        height: formData.height,
        weight: formData.weight,
        educationLevel: formData.educationLevel,
        academicMajor: formData.academicMajor,
        socioeconomicStatus: formData.socioeconomicStatus,
        maritalStatus: formData.maritalStatus,
        career: formData.career,
        householdSize: formData.householdSize,
        gymAccess: formData.gymAccess,
        dietaryRestrictions: formData.dietaryRestrictions,
        disabilities: formData.disabilities,
        injuries: formData.injuries,
        currentHealthIssues: formData.currentHealthIssues,
        medications: formData.medications,
        allergies: formData.allergies,
        photoURL,
        currentCountry: formData.currentCountry,
        currentState: formData.currentState,
        currentCity: formData.currentCity,
        homeCountry: formData.homeCountry,
        homeState: formData.homeState,
        homeCity: formData.homeCity,
        timezone: timezone, // Add inferred timezone
        goals: formData.goals,
        tosConsent: formData.tosConsent,
        researchConsent: formData.researchConsent,
        unitPreference: formData.unitPreference,
      });

      // const progressTitles = [
      //   "Life",
      //   "Exercise",
      //   "Nutrition",
      //   "Social & Emotional",
      // ];
      // progressTitles.forEach((title) => {
      //   const progressDocRef = doc(db, "progress", `${user.uid}_${title}`);
      //   batch.set(progressDocRef, {
      //     uid: user.uid,
      //     title: title,
      //     points: 0,
      //     parent:
      //       title === "Life"
      //         ? null
      //         : ["Morning", "Noon", "Afternoon", "Evening", "Night"].includes(
      //             title
      //           )
      //         ? "1"
      //         : "Life",
      //   });
      // });

      await batch.commit();
      await ensureCoordinatorDrafts(user.uid);
      await sendEmailVerification(user);
      navigate('/verify-email');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <Step1Account
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <Step2Profile
            formData={formData}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (
          <Step3Goals
            formData={formData}
            handleChange={handleChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (
          <Step4Consents
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            prevStep={prevStep}
            uploading={uploading} // Pass uploading state
            error={error} // Pass error state
          />
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <Typography component="h1" variant="h4" align="center">
          Register
        </Typography>
        <Stepper activeStep={currentStep - 1} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {currentStep !== 5 && error && !uploading && ( // Display general errors here if not on consent step or not specific to consent step
             <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                {error}
             </Typography>
        )}
        {renderStepContent(currentStep)}
      </Box>
    </Container>
  );
};

export default MultiStepRegister;
